import { trackServiceBox } from '@portals/tracking/src/content/trackServiceBox';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';

import { ServiceBox } from '../../models';

type ServiceBoxContextValue = {
  open: boolean;
  data?: ServiceBox;
  onOpen: () => void;
  onClose: () => void;
  error: boolean;
};

const ServiceBoxContext = React.createContext<ServiceBoxContextValue | null>(null);

export { ServiceBoxContext };
interface Props {
  children: React.ReactElement | React.ReactElement[];
}

export const ServiceBoxContextProvider: React.FC<Props> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(undefined);
  const [error, setError] = useState<boolean>(false);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!data?.id) {
      return;
    }
    trackServiceBox({
      id: data?.id,
      action: open ? 'open' : 'close',
    });
  }, [data, open]);

  useEffect(() => {
    if (!open || data) {
      return;
    }

    let active = true;

    async function loadData() {
      try {
        const abortTime = +process.env.TIMEOUT_FETCH || 5000;
        const abortController = new AbortController();
        const abortTimeout = setTimeout(() => {
          abortController.abort(`Timeout reached (${abortTime} ms)`);
        }, abortTime);
        const url = new URL(window.location.origin);
        url.pathname = '/api/service-box';
        const { data } = await axios.get(url.toString(), { signal: abortController.signal });

        clearTimeout(abortTimeout);

        if (active) {
          setData(data.data);
        }
      } catch {
        setError(true);
      }
    }

    loadData();

    return () => {
      active = false;
    };
  }, [open, data]);

  useEffect(() => {
    if (open) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [open]);

  return (
    <ServiceBoxContext.Provider value={{ error, open, onOpen: handleOpen, onClose: handleClose, data }}>
      {children}
    </ServiceBoxContext.Provider>
  );
};

export const useServiceBoxContext = (): ServiceBoxContextValue | null => {
  return React.useContext(ServiceBoxContext);
};
